import React from 'react';
import { Banknote, TrendingUp, Clock, Minus, DollarSign } from 'lucide-react';
import { PayrollCutoff } from './technicianTypes';

interface Props {
  cutoff: PayrollCutoff;
  completedJobs: number;
  onRequestAdvance: () => void;
}

export default function LiveSalaryPanel({ cutoff, completedJobs, onRequestAdvance }: Props) {
  const basePay = cutoff.daysWorked * cutoff.dailyRate;
  const otPay = cutoff.totalHoursOT * cutoff.hourlyOTRate;
  const grossPay = basePay + otPay + cutoff.jobIncentives;
  const netPay = grossPay - cutoff.approvedCashAdvance;

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-br from-sky-700 to-sky-900 px-5 py-4 text-white">
        <p className="text-xs text-sky-200 font-600 uppercase tracking-wide">Live Salary Estimate</p>
        <p className="text-sm text-sky-100 mt-0.5">
          Cutoff: {cutoff.month} {cutoff.period}, {cutoff.year}
        </p>
        <p className="text-3xl font-800 tabular-nums mt-2">₱{netPay.toLocaleString()}</p>
        <p className="text-xs text-sky-200 mt-0.5">Estimated net payout</p>
      </div>

      {/* Breakdown */}
      <div className="p-4 space-y-3">
        <p className="text-xs font-600 uppercase tracking-wide text-muted-foreground">Breakdown</p>

        <SalaryRow
          icon={<Clock size={14} />}
          label="Days Worked"
          detail={`${cutoff.daysWorked} days × ₱${cutoff.dailyRate.toLocaleString()}`}
          amount={basePay}
          color="text-foreground"
        />
        <SalaryRow
          icon={<TrendingUp size={14} />}
          label="Overtime Pay"
          detail={`${cutoff.totalHoursOT}h × ₱${cutoff.hourlyOTRate}`}
          amount={otPay}
          color="text-foreground"
        />
        <SalaryRow
          icon={<DollarSign size={14} />}
          label="Job Incentives"
          detail={`${completedJobs} jobs completed`}
          amount={cutoff.jobIncentives}
          color="text-success"
        />

        <div className="border-t border-border pt-2">
          <SalaryRow
            icon={<Banknote size={14} />}
            label="Gross Pay"
            detail=""
            amount={grossPay}
            color="text-foreground"
            bold
          />
        </div>

        <SalaryRow
          icon={<Minus size={14} />}
          label="Cash Advance Deductions"
          detail="Approved advances"
          amount={-cutoff.approvedCashAdvance}
          color="text-danger"
        />

        <div className="border-t-2 border-primary/20 pt-2">
          <SalaryRow
            icon={<Banknote size={14} />}
            label="Estimated Net Pay"
            detail=""
            amount={netPay}
            color="text-primary"
            bold
            large
          />
        </div>

        {cutoff.daysWorked === 0 && (
          <p className="text-xs text-center text-muted-foreground bg-muted rounded-lg py-2 px-3">
            No days logged yet for this cutoff period
          </p>
        )}
      </div>

      {/* Cash advance button */}
      <div className="px-4 pb-4">
        <button
          onClick={onRequestAdvance}
          className="w-full py-3 text-sm font-700 btn-secondary rounded-xl border border-primary/30 flex items-center justify-center gap-2"
        >
          <Banknote size={16} />
          Request Cash Advance
        </button>
        <p className="text-xs text-center text-muted-foreground mt-1.5">
          Based on accumulated earnings for current cutoff
        </p>
      </div>
    </div>
  );
}

function SalaryRow({
  icon, label, detail, amount, color, bold = false, large = false,
}: {
  icon: React.ReactNode;
  label: string;
  detail: string;
  amount: number;
  color: string;
  bold?: boolean;
  large?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">{icon}</span>
        <div>
          <p className={`text-sm ${bold ? 'font-700' : 'font-500'} text-foreground`}>{label}</p>
          {detail && <p className="text-xs text-muted-foreground">{detail}</p>}
        </div>
      </div>
      <span className={`tabular-nums font-700 ${large ? 'text-lg' : 'text-sm'} ${color}`}>
        {amount < 0 ? '-' : ''}₱{Math.abs(amount).toLocaleString()}
      </span>
    </div>
  );
}